import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { HeroVariant, PresenceHeroContent } from '~/marketing/presenceContract';
import {
  marketingAnchors,
  marketingDoorHeroes,
  marketingHero,
  marketingHomeHero,
  marketingWaitlistSocialProof,
  type MarketingDoorId,
} from '~/marketing/landingContent';
import { hasHeroClusterRaster } from '../assets/marketing/hero/heroClusterExport';
import { CircleForming } from '../components/marketing/CircleForming';
import { HeroEmailCapture } from '../components/marketing/HeroEmailCapture';
import { HeroMarketingVisual } from '../components/marketing/HeroMarketingVisual';
import { StatTicker } from '../components/marketing/StatTicker';
import { marketingVisualVariant } from '../config';
import { useScrollParallax } from '../hooks/useScrollParallax';
import { GENERIC_ACTIVITY_FALLBACK } from './presenceStaticHero';
import { useReducedMotion } from './useReducedMotion';
import '../components/ember/ember.css';

const INTERVAL_MS = 5000;
const TRANSITION_MS = 200;

type PresenceHeroProps = {
  wide: boolean;
  variant: HeroVariant;
  hero: PresenceHeroContent;
  /** For variant C: prefer this over hero.activityLine */
  activityLineOverride?: string;
  motionKilled: boolean;
  disableAsideFloat: boolean;
  isLoading: boolean;
  /** Night-city hero scene (glyph + phone + sparks) instead of raster / variant asides */
  brandScene?: boolean;
  /**
   * Door campaign id (marketingDoorHeroes). Overrides headline/sub/trust and adds the proof-chip
   * row + note line in BOTH hero modes — brandScene reads marketingHero.* directly, so a
   * prop-only override would silently show the default headline exactly where production
   * renders. Also suffixes the inline waitlist capture's source so door attribution reaches the
   * hero's actual conversion surface.
   */
  door?: MarketingDoorId;
  /** When false, hero shows inline email capture instead of scroll-to-waitlist CTA */
  showAppDownload?: boolean;
  onScrollToAnchor: (id: string) => void;
  onPrimaryCtaClick?: () => void;
  onSecondaryCtaClick?: () => void;
  onHeroInView: () => void;
};

function hashToAnchorId(href: string): string | null {
  if (href.startsWith('#') && href.length > 1) return href.slice(1);
  return null;
}

function VariantARotates({
  responses,
  cardEyebrow,
  prompt,
  cardNote,
  motionKilled,
  userReducedMotion,
  disableFloat,
  onViewIndex,
}: {
  responses: string[];
  cardEyebrow: string;
  prompt: string;
  cardNote: string;
  motionKilled: boolean;
  userReducedMotion: boolean;
  disableFloat: boolean;
  onViewIndex: (i: number) => void;
}) {
  const safe = useMemo(() => responses.filter(Boolean).slice(0, 12), [responses]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hiding, setHiding] = useState(false);

  const noMotion = motionKilled || userReducedMotion;
  const showRotate = !noMotion && safe.length > 1;

  useEffect(() => {
    onViewIndex(noMotion || safe.length < 1 ? 0 : index);
  }, [index, onViewIndex, noMotion, safe.length]);

  useEffect(() => {
    if (!showRotate || paused) return;
    const t = setInterval(() => {
      setHiding(true);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % safe.length);
        setHiding(false);
      }, TRANSITION_MS);
    }, INTERVAL_MS);
    return () => clearInterval(t);
  }, [showRotate, paused, safe.length]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) setPaused(true);
      else setPaused(false);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const current = safe.length ? (noMotion ? safe[0] : safe[index % safe.length]) : '';

  const ghost = useMemo(() => {
    if (safe.length < 2) return [] as string[];
    const g: string[] = [];
    for (let n = 1; n < safe.length && g.length < 2; n++) {
      g.push(safe[(index + n) % safe.length]);
    }
    return g;
  }, [safe, index]);

  return (
    <aside
      className="ml-hero__aside ml-heroIn ml-heroIn--4"
      aria-label="Circle moment card"
      data-testid="presence-hero-aside-a">
      <div
        className={`ml-moment ${disableFloat ? '' : 'ml-moment--motion'}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onPointerDownCapture={() => setPaused(true)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setPaused(false);
          }
        }}>
        {cardEyebrow ? <p className="ml-moment__eyebrow">{cardEyebrow}</p> : null}
        {prompt ? <p className="ml-moment__promptLine">{prompt}</p> : null}
        {current ? (
          <div
            role="status"
            aria-atomic="true"
            className={
              'ml-rotatingResponse ' +
              (hiding ? 'ml-rotatingResponse--out' : 'ml-rotatingResponse--in')
            }>
            {current}
          </div>
        ) : null}
        {ghost.length > 0 ? (
          <div className="ml-rotatingGhosts" aria-hidden>
            {ghost.map((g) => (
              <span key={g} className="ml-rotatingGhost">
                {g}
              </span>
            ))}
          </div>
        ) : null}
        {cardNote ? <p className="ml-moment__aside">{cardNote}</p> : null}
      </div>
    </aside>
  );
}

function VariantBOrbs({
  hero,
  disableFloat,
}: {
  hero: PresenceHeroContent;
  disableFloat: boolean;
}) {
  const prompt = hero.prompt || '“What’s something you’re looking forward to this month?”';
  const note = hero.supportingLine || hero.cardNote || '';
  return (
    <aside className="ml-hero__aside ml-heroIn ml-heroIn--4" aria-label="Circle prompt preview">
      <div className={`ml-heroBOrbs ${disableFloat ? '' : 'ml-heroBOrbs--float'}`}>
        <div className="ml-heroBOrbs__card">
          <p className="ml-moment__eyebrow">{hero.promptCardLabel || 'Circle prompt'}</p>
          <p className="ml-moment__quote" style={{ marginTop: 8 }}>
            {prompt}
          </p>
          {note ? (
            <p className="ml-moment__aside" style={{ marginTop: 12 }}>
              {note}
            </p>
          ) : null}
        </div>
      </div>
    </aside>
  );
}

function ControlMomentCard({
  hero,
  disableFloat,
}: {
  hero: PresenceHeroContent;
  disableFloat: boolean;
}) {
  return (
    <aside
      className="ml-hero__aside ml-heroIn ml-heroIn--4"
      aria-label="Example of a circle prompt"
      data-testid="presence-hero-aside-control">
      <div className={`ml-moment ${disableFloat ? '' : 'ml-moment--motion'}`}>
        {hero.cardEyebrow ? <p className="ml-moment__eyebrow">{hero.cardEyebrow}</p> : null}
        {hero.prompt ? <p className="ml-moment__quote">{hero.prompt}</p> : null}
        {hero.cardNote ? <p className="ml-moment__aside">{hero.cardNote}</p> : null}
      </div>
    </aside>
  );
}

function VariantCAside({
  hero,
  disableFloat,
}: {
  hero: PresenceHeroContent;
  disableFloat: boolean;
}) {
  return (
    <aside
      className="ml-hero__aside ml-heroIn ml-heroIn--4"
      aria-label="Example of a circle prompt">
      <div className={`ml-moment ${disableFloat ? '' : 'ml-moment--motion'}`}>
        {hero.cardEyebrow ? <p className="ml-moment__eyebrow">{hero.cardEyebrow}</p> : null}
        {hero.prompt ? <p className="ml-moment__quote">{hero.prompt}</p> : null}
        {hero.cardNote ? <p className="ml-moment__aside">{hero.cardNote}</p> : null}
      </div>
    </aside>
  );
}

function AsideSkeleton() {
  return (
    <aside className="ml-hero__aside" aria-hidden>
      <div className="ml-moment ml-presenceSkel" />
    </aside>
  );
}

function ActivityPill({
  line,
  motionKilled,
  userReduced,
}: {
  line: string;
  motionKilled: boolean;
  userReduced: boolean;
}) {
  const [out, setOut] = useState(false);
  const [shown, setShown] = useState(line);
  const still = motionKilled || userReduced;
  useEffect(() => {
    if (still || line === shown) return;
    setOut(true);
    const t = window.setTimeout(() => {
      setShown(line);
      setOut(false);
    }, TRANSITION_MS);
    return () => clearTimeout(t);
  }, [line, shown, still]);
  return (
    <p
      className={
        'ml-activityPill ' + (out && !still ? 'ml-activityPill--out' : 'ml-activityPill--in')
      }
      role="status"
      aria-live="polite"
      style={{ minHeight: 28 }}>
      <span className="ml-activityPill__dot" aria-hidden />
      {shown}
    </p>
  );
}

export function PresenceHero({
  wide,
  variant,
  hero,
  activityLineOverride,
  motionKilled,
  disableAsideFloat,
  isLoading,
  brandScene = false,
  door,
  showAppDownload = false,
  onScrollToAnchor,
  onPrimaryCtaClick,
  onSecondaryCtaClick,
  onHeroInView,
}: PresenceHeroProps) {
  const userRm = useReducedMotion();
  const noMotion = motionKilled || userRm;
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const heroCopyRef = useRef<HTMLDivElement | null>(null);
  const heroInViewOnce = useRef(false);

  useScrollParallax(heroCopyRef, 0.03, {
    disabled: noMotion || !brandScene,
    maxPx: 20,
    invert: true,
  });

  const activityLine = activityLineOverride ?? hero.activityLine ?? '';

  const renderSecondary = () => {
    if (!hero.secondaryCta) return null;
    const { href, label } = hero.secondaryCta;
    const id = hashToAnchorId(href);
    if (id) {
      return (
        <button
          type="button"
          className="ml-btn ml-btn--outline"
          onClick={() => {
            onSecondaryCtaClick?.();
            if (id) onScrollToAnchor(id);
          }}>
          {label}
        </button>
      );
    }
    return (
      <a className="ml-btn ml-btn--outline" href={href} onClick={() => onSecondaryCtaClick?.()}>
        {label}
      </a>
    );
  };

  const onViewIdx = useCallback((i: number) => {
    void i;
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting && e.intersectionRatio >= 0.5 && !heroInViewOnce.current) {
          heroInViewOnce.current = true;
          onHeroInView();
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onHeroInView]);

  // Per-line clip-up reveal for the brand headline (transition-driven so it doesn't clobber the
  // accent line's gradient shimmer; a class flip on mount triggers it — reduced-motion shows it flat).
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  useEffect(() => {
    if (!brandScene) return;
    const el = headlineRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => el.classList.add('is-revealed'));
    // Safety net: guarantee the headline becomes visible even if the rAF is dropped.
    const fallback = window.setTimeout(() => el.classList.add('is-revealed'), 1200);
    return () => {
      cancelAnimationFrame(id);
      window.clearTimeout(fallback);
    };
  }, [brandScene]);

  const doorHero = door ? marketingDoorHeroes[door] : undefined;
  // Un-doored "/" in brandScene mode reads marketingHomeHero (the weekly-occasion story), not
  // marketingHero (the native app's welcome-door copy via outcomeMessaging.ts) — see the comment
  // on marketingHomeHero in landingContent.ts. Same shape as MarketingDoorHero, so it drives the
  // proof-chip row and note line below exactly like a door hero does.
  const homeHero = !doorHero && brandScene ? marketingHomeHero : undefined;
  const activeHero = doorHero ?? homeHero;
  const headlineSource = activeHero?.h1 ?? (brandScene ? marketingHero.h1 : hero.headline);
  const headlineLines = headlineSource.split('\n').filter(Boolean);
  const accentIndex = activeHero
    ? activeHero.h1AccentLineIndex
    : brandScene
      ? marketingHero.h1AccentLineIndex
      : -1;
  const subheadSource = activeHero?.sub ?? (brandScene ? marketingHero.sub : hero.subhead);
  const showInlineWaitlist = brandScene && !showAppDownload;

  const renderLeftColumn = () => (
    <div
      ref={brandScene ? heroCopyRef : undefined}
      className={brandScene ? 'hero-copy ml-parallaxScope' : 'ml-hero__text'}>
      <div className={brandScene ? 'ml-parallaxLift' : undefined}>
        {hero.eyebrow ? <p className="ml-kicker ml-heroIn ml-heroIn--1">{hero.eyebrow}</p> : null}
        <h1
          ref={brandScene ? headlineRef : undefined}
          className={brandScene ? undefined : 'ml-h1 ml-heroIn ml-heroIn--2'}>
          {brandScene ? (
            <>
              {headlineLines.map((line, i) => (
                <span
                  key={line}
                  className={i === accentIndex ? 'accent nowrap ml-gradient-text' : undefined}>
                  {line}
                </span>
              ))}
            </>
          ) : (
            headlineLines.map((line, i) => (
              <span
                key={i}
                className={
                  'eb-hero__h1Line' + (i === accentIndex ? ' eb-hero__h1Line--accent' : '')
                }>
                {line}
              </span>
            ))
          )}
        </h1>
        {brandScene ? (
          subheadSource.split('\n\n').map((para, i) => (
            <p
              key={para}
              className={
                'hero-copy__sub' + (i === 0 ? ' hero-copy__sub--lead' : ' hero-copy__sub--muted')
              }>
              {para.split('\n').map((line, lineIndex, lines) => (
                <span key={line}>
                  {line}
                  {lineIndex < lines.length - 1 ? <br /> : null}
                </span>
              ))}
            </p>
          ))
        ) : (
          // subheadSource, not hero.subhead — the door override must hold in BOTH hero modes.
          <p className="ml-sub ml-heroIn ml-heroIn--3">{subheadSource}</p>
        )}
        <div className="ml-heroCtas ml-heroIn ml-heroIn--4">
          {showInlineWaitlist ? (
            // The inline capture passes an explicit source (bypassing the marked-source
            // singleton), so door attribution must ride the source string itself.
            <HeroEmailCapture
              source={door ? `landing-hero-inline-${door}` : 'landing-hero-inline'}
            />
          ) : (
            <a
              className="ml-btn ml-btn--primary eb-cta eb-cta--primary"
              href={hero.primaryCta.href}
              onClick={() => onPrimaryCtaClick?.()}>
              {hero.primaryCta.label}
            </a>
          )}
          {renderSecondary()}
        </div>
        {brandScene && !showAppDownload ? (
          <p className="hero-copy__social-proof">{marketingWaitlistSocialProof}</p>
        ) : null}
        {brandScene && (activityLine || GENERIC_ACTIVITY_FALLBACK) ? (
          <StatTicker
            line={activityLine || GENERIC_ACTIVITY_FALLBACK}
            motionKilled={motionKilled}
          />
        ) : null}
        {brandScene ? (
          <p className="hero-copy__trust">{activeHero?.trust ?? marketingHero.trust}</p>
        ) : null}
        {variant === 'C' && activityLine && !brandScene ? (
          !noMotion ? (
            <ActivityPill line={activityLine} motionKilled={motionKilled} userReduced={userRm} />
          ) : (
            <p className="ml-activityPillText">{activityLine}</p>
          )
        ) : null}
        {!brandScene && (doorHero?.trust ?? hero.trustLine) ? (
          <p className="ml-trust ml-heroIn ml-heroIn--5">{doorHero?.trust ?? hero.trustLine}</p>
        ) : null}
        {activeHero ? (
          <ul className="ml-heroChips" aria-label="What Kynward is">
            {activeHero.proofChips.map((chip) => (
              <li key={chip} className="ml-heroChip">
                {chip}
              </li>
            ))}
          </ul>
        ) : null}
        {activeHero?.note ? (
          // Above-fold LGBTQ+ signal on the him/her doors — a queer visitor should not have to
          // scroll to learn the product includes them.
          <button
            type="button"
            className="ml-heroDoorNote"
            onClick={() => onScrollToAnchor(marketingAnchors[activeHero.note.anchorKey])}>
            {activeHero.note.text} <span aria-hidden>→</span>
          </button>
        ) : null}
      </div>
    </div>
  );

  const renderAside = () => {
    if (brandScene) {
      return <CircleForming motionKilled={noMotion} door={door} />;
    }
    if (isLoading) return <AsideSkeleton />;
    if (marketingVisualVariant() === 'social_visuals_v1') {
      return (
        <aside
          className="ml-hero__aside ml-heroVisual ml-heroIn ml-heroIn--4"
          aria-label="Example Kynward circle moment"
          data-testid="hero-marketing-visual">
          <div className="ml-marketingVisual ml-marketingVisual--hero">
            <HeroMarketingVisual
              classNameImage="ml-marketingVisual__img"
              alt={
                hasHeroClusterRaster()
                  ? 'Group of people nearby in a calm, low-pressure moment — Kynward.'
                  : 'Example Kynward circle prompt with three friendly responses from nearby people.'
              }
            />
          </div>
        </aside>
      );
    }
    if (variant === 'control') {
      return <ControlMomentCard hero={hero} disableFloat={disableAsideFloat} />;
    }
    if (variant === 'A') {
      return (
        <VariantARotates
          responses={hero.responses?.length ? hero.responses : ['']}
          cardEyebrow={hero.cardEyebrow || 'A moment from a Circle'}
          prompt={hero.prompt || ''}
          cardNote={hero.cardNote || ''}
          motionKilled={noMotion}
          userReducedMotion={userRm}
          disableFloat={disableAsideFloat}
          onViewIndex={onViewIdx}
        />
      );
    }
    if (variant === 'B') {
      return <VariantBOrbs hero={hero} disableFloat={disableAsideFloat} />;
    }
    if (variant === 'C') {
      return <VariantCAside hero={hero} disableFloat={disableAsideFloat} />;
    }
    return <ControlMomentCard hero={hero} disableFloat={disableAsideFloat} />;
  };

  if (brandScene) {
    return (
      <div ref={sectionRef} className="ml-presenceRoot eb-hero--grid" data-testid="presence-hero">
        {renderLeftColumn()}
        {renderAside()}
      </div>
    );
  }

  return (
    <div ref={sectionRef} className="ml-presenceRoot" data-testid="presence-hero">
      <div className="ml-hero" style={{ flexDirection: wide ? 'row' : 'column' }}>
        {renderLeftColumn()}
        {renderAside()}
      </div>
    </div>
  );
}
